import { useCallback, useState } from 'react';
import { Home, Plus, Radio, Search, X } from 'lucide-react';

// Suggested location: src/ui/screens/post/widgets/PostAddFab.jsx

const ORANGE = '#FF8C08';
const CARD = '#23262C';

/**
 * The three things a user can post from the Post screen.
 * Mirrors Dart's `PostAddOption` enum.
 */
export const PostAddOption = {
  LISTING: 'listing',
  WANT: 'want',
  STATUS: 'status',
};

const OPTIONS = [
  {
    value: PostAddOption.STATUS,
    icon: Radio,
    label: 'Status',
    subtitle: 'Share a quick text or photo update',
  },
  {
    value: PostAddOption.WANT,
    icon: Search,
    label: 'Want',
    subtitle: "Post what you're looking for",
  },
  {
    value: PostAddOption.LISTING,
    icon: Home,
    label: 'Listing',
    subtitle: 'Post a property, service or item',
  },
];

/**
 * Drop this in exactly where a plain FloatingActionButton used to sit.
 * It preserves the same show/hide behaviour (slide + fade driven by
 * `visible`, tied to the Posted-tab scroll direction) and renders as
 * `position: fixed` at the viewport level — not relative to a scrolling
 * container — with a z-index high enough to sit above the app's bottom
 * nav bar. That's the fix for it being hidden behind the nav: the old
 * button was `position: absolute` inside the scrolling page content, so
 * a fixed, higher-stacked bottom nav could cover it.
 *
 * Tapping it pops out three options above a blurred backdrop — same
 * blur treatment as the category sub-menu — and swaps the "+" for an
 * "X" to close. `onSelect` is called with one of the `PostAddOption`
 * values.
 *
 * NOTE: `--paf-bottom` below assumes an ~64px mobile bottom nav; adjust
 * that one number to match your actual BottomNavBar height.
 */
export default function PostAddFab({ visible, onSelect }) {
  const [open, setOpen] = useState(false);

  const close = useCallback(() => setOpen(false), []);

  const select = useCallback(
    (value) => {
      setOpen(false);
      // Let the close transition read before navigating away.
      setTimeout(() => onSelect?.(value), 150);
    },
    [onSelect],
  );

  return (
    <>
      <style>{CSS}</style>

      <button
        className="paf-fab"
        style={{
          transform: visible ? 'translateY(0)' : 'translateY(140%)',
          opacity: visible ? 1 : 0,
          pointerEvents: visible ? 'auto' : 'none',
        }}
        onClick={() => setOpen(true)}
        aria-label="Add"
      >
        <Plus size={30} color="#000" />
      </button>

      {open && (
        <div className="paf-overlay">
          <div className="paf-backdrop" onClick={close} />

          <div className="paf-options">
            {OPTIONS.map((opt, i) => {
              const Icon = opt.icon;
              return (
                <button
                  key={opt.value}
                  className="paf-pill"
                  style={{ animationDelay: `${i * 60}ms` }}
                  onClick={() => select(opt.value)}
                >
                  <div className="paf-pill-text">
                    <span className="paf-pill-label">{opt.label}</span>
                    <span className="paf-pill-subtitle">{opt.subtitle}</span>
                  </div>
                  <div className="paf-pill-icon">
                    <Icon size={19} color={ORANGE} />
                  </div>
                </button>
              );
            })}
          </div>

          <button className="paf-close" onClick={close} aria-label="Close">
            <X size={28} color="#000" />
          </button>
        </div>
      )}
    </>
  );
}

const CSS = `
.paf-fab{position:fixed;right:16px;bottom:calc(64px + 20px + env(safe-area-inset-bottom));width:56px;height:56px;border-radius:28px;background:${ORANGE};border:none;display:flex;align-items:center;justify-content:center;cursor:pointer;padding:0;box-shadow:0 6px 18px rgba(255,140,8,.35);z-index:70;transition:transform 250ms ease-in-out,opacity 250ms ease-in-out;}
.paf-fab:hover{filter:brightness(1.06);}

.paf-overlay{position:fixed;inset:0;z-index:80;}
.paf-backdrop{position:absolute;inset:0;background:rgba(0,0,0,.55);backdrop-filter:blur(18px);-webkit-backdrop-filter:blur(18px);animation:pafFade 260ms ease-out;}
@keyframes pafFade{from{opacity:0;}to{opacity:1;}}

.paf-options{position:fixed;right:16px;bottom:calc(64px + 20px + env(safe-area-inset-bottom) + 70px);display:flex;flex-direction:column;align-items:flex-end;gap:14px;z-index:81;}
.paf-pill{display:flex;align-items:center;gap:12px;padding:12px 16px;background:${CARD};border:1px solid rgba(255,140,8,.30);border-radius:28px;box-shadow:0 4px 14px rgba(0,0,0,.30);cursor:pointer;animation:pafPop 320ms cubic-bezier(.2,.9,.25,1.3) both;}
@keyframes pafPop{from{opacity:0;transform:scale(.85) translateY(6px);}to{opacity:1;transform:scale(1) translateY(0);}}
.paf-pill-text{display:flex;flex-direction:column;align-items:flex-end;}
.paf-pill-label{color:#fff;font-size:14px;font-weight:700;}
.paf-pill-subtitle{color:rgba(255,255,255,.38);font-size:10px;margin-top:1px;white-space:nowrap;}
.paf-pill-icon{width:38px;height:38px;border-radius:50%;background:rgba(255,140,8,.15);display:flex;align-items:center;justify-content:center;flex:none;}

.paf-close{position:fixed;right:16px;bottom:calc(64px + 20px + env(safe-area-inset-bottom));width:56px;height:56px;border-radius:28px;background:${ORANGE};border:none;display:flex;align-items:center;justify-content:center;cursor:pointer;padding:0;z-index:82;animation:pafRotate 320ms cubic-bezier(.2,.9,.25,1.3) both;}
@keyframes pafRotate{from{transform:rotate(-45deg) scale(.9);opacity:.4;}to{transform:rotate(0deg) scale(1);opacity:1;}}

/* Desktop: PostScreen wraps this component in lg:hidden, so these rules
   only matter if you ever render PostAddFab without that wrapper. */
@media (min-width:1024px){
  .paf-fab,.paf-close{bottom:calc(24px + env(safe-area-inset-bottom));}
  .paf-options{bottom:calc(24px + env(safe-area-inset-bottom) + 70px);}
}
@media (prefers-reduced-motion:reduce){
  .paf-pill,.paf-close,.paf-backdrop{animation:none;}
}
`;